import React from 'react';
import { Helmet } from 'react-helmet-async';
import Hero from '@/components/Hero';
import TeamMember from '@/components/TeamMember';
import FeatureCard from '@/components/FeatureCard';
import { Link } from 'react-router-dom';
import { Calendar, Map, Users } from 'lucide-react';

const About = () => {
  // Tour guides data with updated images
  const teamMembers = [
    {
      name: "Peggy Gavan",
      role: "Co-Founder and Licensed Tour Guide",
      bio: "Peggy Gavan is not only a talented author but also a licensed New York City tour guide and a member of the steering committee for the future Cat Museum of New York City. Peggy has a knack for bringing history to life, specializing in feline and other animal tales of Old New York.",
      image: "/lovable-uploads/945db7f1-e659-4f6a-98ab-b9f0d8e134c8.png", 
      catName: "Jack, Jeter and Misha"
    },
    {
      name: "Dan Rimada",
      role: "Co-Founder",
      bio: "Dan Rimada is the founder of Bodega Cats of New York and the co-founder of Cats About Town Tours. He leads operations and marketing for the company, working behind the scenes to build partnerships, craft the brand voice, and make sure everything runs smoothly. His love for NYC and its cat-filled history drives the creative direction and growth of the tours.",
      image: "/lovable-uploads/4dc12852-950b-4c4b-bf57-f0e03e94d96e.png", 
      catName: "Cocoa & Charles"
    },
    {
      name: "Jenny Pierson",
      role: "Licensed Tour Guide",
      bio: "Jenny Pierson is a tour guide and the founding executive director of the forthcoming Cat Museum of New York City (CMNYC), which will highlight the cultural history of cats through art exhibitions and education. A lifelong advocate for cat welfare and a proud TNR-certified cat rescuer.",
      image: "/lovable-uploads/22046e44-91a6-41fb-8492-cb6f6b0a9c1e.png",
      catName: "Churro"
    },
    {
      name: "Marie Carter",
      role: "Licensed Tour Guide",
      bio: "Marie Carter has been a New York City tour guide for over a decade and is the author of three books, including Mortimer and the Witches: A History of Nineteenth-Century Fortune Tellers. Originally from Scotland, she grew up in a home filled with cats and now lives in Astoria, Queens.",
      image: "/lovable-uploads/ec624c9c-eb56-498c-9276-8fab4e193253.png",
      catName: "Elsa & Grayson"
    }
  ];

  // Mission features
  const missionFeatures = [
    {
      title: "Heartwarming Cat Tales",
      description: "Discover the enchanting stories of cats that have left their mark on NYC's history.",
      icon: <Map className="w-5 h-5 text-accent" />
    },
    {
      title: "Expert Guidance",
      description: "Enjoy tours personally curated by cat historian Peggy Gavan and led by our team of expert licensed guides—passionate storytellers who bring each historical feline tale to life.",
      icon: <Users className="w-5 h-5 text-accent" />
    },
    {
      title: "Iconic Locations",
      description: "Visit notable sites such as the Brooklyn Bridge, Borough Hall, and the Promenade, all tied to fascinating cat tales.",
      icon: <Map className="w-5 h-5 text-accent" />
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      <Helmet>
        <title>About Us | Cats About Town</title>
        <meta name="description" content="Learn about our expert team of feline historians and guides. Discover the story behind NYC's premier cat history walking tours." />
        <meta name="keywords" content="about cats about town, nyc cat tour guides, feline historians, cat walking tour team" />
        <link rel="canonical" href="https://catsabouttowntours.com/about" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://catsabouttowntours.com/" },
              { "@type": "ListItem", "position": 2, "name": "About Us", "item": "https://catsabouttowntours.com/about" }
            ]
          })}
        </script>
      </Helmet>
      
      {/* Hero Section - Updated with proper image position */}
      <Hero 
        title="About Us" 
        subtitle="Welcome to Cats About Town: NYC Walking Tours for Cat Lovers"
        backgroundImage="/lovable-uploads/5dc7d0e7-c190-437a-959d-dc507bc15d70.png"
        height="medium"
        imageStyle="darken"
        imagePosition="top" // Adjusted to better show the people
      />
      
      {/* Our Mission Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div className="inline-block px-4 py-1 bg-accent/10 rounded-full text-accent text-sm font-medium mb-4">Our Story</div>
            <h2 className="text-3xl md:text-4xl font-montserrat font-bold mb-6 text-primary">About Our Tours</h2>
            <p className="text-xl font-medium mb-6 text-accent italic">
              Where History's Most Notable Felines Come to Life Through Stories
            </p>
            <p className="text-xl font-medium mb-6 text-accent italic">
              Every Historical Cat Has a Tale, Let's Relive It Together
            </p>
            <div className="prose prose-lg max-w-3xl mx-auto text-neutral-gray-700">
              <p className="mb-6">
                At Cats About Town Tours, we don't just walk through history—we immerse ourselves in it. Every step reveals a tale of bravery, curiosity, and heart, as we bring the stories of New York City's legendary felines to life. Our tours aren't just for the cat-obsessed or history buffs; they're for anyone who wants to see the city through a lens that's both unique and utterly captivating.
              </p>
              <p className="mb-6">
                Our tours focus on the fascinating historical tales of NYC's cats from yesteryear. While we might spot a modern-day feline friend peeking from a window along our route (no guarantees—cats are notoriously independent!), our walks primarily celebrate the whiskers of the past through engaging storytelling at historic locations.
              </p>
              <p className="mb-6">
                <strong className="text-primary">Walk Through Time with Us</strong><br/>
                Each tour is meticulously curated by Peggy Gavan, our celebrated cat historian and master storyteller, and brought to life by our team of passionate, licensed guides. Step into a deep dive into the hidden world of NYC's cats from the 1800s and early 1900s. Think of it as exploring the pages of history—these aren't your modern Instagram cats, but the historical felines who helped shape New York's neighborhoods long before social media existed!
              </p>
              <p>
                <strong className="text-primary">Discover the Heroes of NYC's Past</strong><br/>
                Join us as we unveil the heartwarming and awe-inspiring stories of New York City's feline residents. From the legendary mousers of Brooklyn Heights to the celebrated companions of Manhattan's most prominent historical figures, you'll see the city's history through a new, enchanting lens—one where every cat has its moment in the spotlight.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Meet the Team Section */}
      <section className="py-16 bg-neutral-tan relative overflow-hidden">
        <div className="absolute inset-0 bg-paw-pattern opacity-5"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="inline-block px-4 py-1 bg-accent/10 rounded-full text-accent text-sm font-medium mb-4 mx-auto block text-center">Our Experts</div>
          <h2 className="text-3xl md:text-4xl font-montserrat font-bold text-center mb-12 text-primary">
            Meet the Team
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member, index) => (
              <div key={index} className="animate-fade-in" style={{ animationDelay: `${index * 150}ms` }}>
                <TeamMember 
                  name={member.name}
                  role={member.role}
                  bio={member.bio}
                  image={member.image}
                  catName={member.catName}
                />
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Our Mission - Updated with icons and modern design */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div className="inline-block px-4 py-1 bg-accent/10 rounded-full text-accent text-sm font-medium mb-4">Why Choose Us</div>
            <h2 className="text-3xl md:text-4xl font-montserrat font-bold mb-8 text-primary">Our Mission</h2>
            <p className="text-lg mb-8">
              Our expert guides bring the history of NYC's legendary cats to life, making you feel the wonder and charm in every step. Expect to be moved, to laugh, and to see New York in a way you've never imagined.
            </p>
            <p className="text-lg mb-8">
              Our tours are designed for history enthusiasts and cat lovers alike. While we can't promise live cat sightings (they rarely respect our tour schedules!), we guarantee you'll leave with a head full of delightful historical cat tales that will change how you see the city's past.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {missionFeatures.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl shadow-elegant p-6 border border-gray-100 transition-all duration-300 hover:shadow-premium hover:-translate-y-1">
                <div className="bg-accent/10 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3 text-primary">{feature.title}</h3>
                <p className="text-neutral-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <p className="mb-6">
              Ready to embark on a historical cat-tale adventure? Our walking tours are waiting! Get in touch to book your tour or ask any questions. We're here to help and excited to share these stories with you!
            </p>
            <Link 
              to="/booking" 
              className="bg-accent text-white px-6 py-3 rounded-lg font-medium transition-all duration-300 hover:scale-105 hover:-translate-y-1 hover:shadow-lg inline-flex items-center"
            >
              <Calendar className="mr-2" size={20} />
              Reserve your tour today
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
